import { ReactNode, useEffect } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Mobile-first modal: an animated bottom sheet on small screens, a scale-fade
 * card on larger ones. Closes on backdrop click or Escape. Honors reduced motion.
 */
const Modal: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}> = ({ open, onClose, title, children }) => {
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const enter = reduce ? { opacity: 0 } : { opacity: 0, y: 28, scale: 0.98 };
  const shown = reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="max-h-[92vh] w-full overflow-y-auto rounded-t-4xl bg-paper shadow-warm-lg ring-1 ring-primary-100 sm:max-w-md sm:rounded-4xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            initial={enter}
            animate={shown}
            exit={enter}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
          >
            <div className="flex justify-center pt-3 sm:hidden">
              <span className="h-1.5 w-10 rounded-full bg-ink/15" />
            </div>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-primary-100 bg-paper/95 px-5 py-4 backdrop-blur">
              <h2 className="font-display text-xl font-semibold text-ink">{title}</h2>
              <button
                onClick={onClose}
                aria-label="Close"
                className="rounded-full px-1 text-2xl leading-none text-ink/40 transition-colors hover:bg-ink/5 hover:text-ink/70"
              >
                ×
              </button>
            </div>
            <div className="p-5">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;
